import React, { useEffect, useState, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  NewReleases as NewReleasesIcon,
  AccessTime as AccessTimeIcon,
  EventNote as EventNoteIcon,
  AttachMoney as AttachMoneyIcon,
  Repeat as RepeatIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import { collection, getDocs, getDoc, query, where, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { db, functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import CreateReservationDialog from '../components/CreateReservationDialog';
import { format, subDays, subMonths, differenceInDays, startOfMonth, endOfMonth, eachMonthOfInterval, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { fetchCustomersFromLedger } from '../utils/customerLedger';

// Types
interface Customer {
  id: string;
  userId: string;
  email: string;
  displayName: string;
  phoneNumber: string;
  userLineId?: string;
  stats: {
    totalReservations: number;
    cancelledReservations: number;
    noShowCount: number;
    completedReservations: number;
    totalSpent: number;
  };
  createdAt: Date;
  lastVisitAt?: Date;
  firstVisitAt?: Date;
  preferredDayOfWeek?: number;
  preferredTimeSlot?: string;
  averageLeadTime?: number;
}

interface CustomerReservation {
  id: string;
  reservationDate: Date;
  startTime: string;
  endTime: string;
  menuName: string;
  staffName: string | null;
  status: string;
  totalPrice: number;
  createdAt?: Date;
}

interface CustomerSegment {
  name: string;
  count: number;
  percentage: number;
  color: string;
  icon: React.ReactNode;
  description: string;
  [key: string]: string | number | React.ReactNode;
}

interface VisitPattern {
  dayOfWeek: string;
  count: number;
  [key: string]: string | number;
}

interface TimeSlotPattern {
  timeSlot: string;
  count: number;
  [key: string]: string | number;
}

interface MonthlyStats {
  month: string;
  newCustomers: number;
  repeatCustomers: number;
  totalCustomers: number;
  repeatRate: number;
  [key: string]: string | number;
}

interface LeadTimeDistribution {
  range: string;
  count: number;
  percentage: number;
  [key: string]: string | number;
}

const COLORS = ['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4', '#795548', '#607D8B'];
const SEGMENT_COLORS = {
  vip: '#FFD700',
  regular: '#4CAF50',
  new: '#2196F3',
  dormant: '#9E9E9E',
  atrisk: '#FF9800',
  churned: '#F44336',
};

const Customers: React.FC = () => {
  const { shop } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [allReservations, setAllReservations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerReservations, setCustomerReservations] = useState<CustomerReservation[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openCreateReservationDialog, setOpenCreateReservationDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [detailTabValue, setDetailTabValue] = useState(0);

  // Main tabs: List vs Analytics
  const [mainTabValue, setMainTabValue] = useState(0);
  const [analyticsSubTab, setAnalyticsSubTab] = useState(0);
  const [segmentFilter, setSegmentFilter] = useState<string>('all');

  // 会計用の顧客選択モードかどうか
  const selectMode = (location.state as { selectMode?: string } | null)?.selectMode;
  const isCheckoutSelectMode = selectMode === 'checkout';

  // 顧客を選択して会計画面に戻る
  const handleSelectForCheckout = (customer: Customer) => {
    navigate('/mobile-order/checkout', {
      state: {
        selectedCustomer: {
          id: customer.id,
          name: customer.displayName,
        }
      }
    });
  };

  useEffect(() => {
    fetchCustomers();
  }, [shop]);

  const fetchCustomers = async () => {
    if (!shop) return;

    try {
      setLoading(true);

      // 以前はここで reservations（予約）から顧客を組み立てていた。
      // 予約を使わない業態では常に空になり、「顧客がいません」しか出なかった。
      // 会計・未収金・ボトルキープに残った名前から作る。
      const ledger = await fetchCustomersFromLedger(shop.id);

      // 予約前提の画面の形に詰め直す。
      // 「予約回数」は「来店回数」、「累計売上」はそのまま使える。
      const customersArray = ledger.map((c) => ({
        id: c.id,
        userId: c.id,
        email: '',
        displayName: c.displayName,
        phoneNumber: c.phoneNumber,
        stats: {
          totalReservations: c.visitCount,
          cancelledReservations: 0,
          noShowCount: 0,
          completedReservations: c.visitCount,
          totalSpent: c.totalSpent,
        },
        createdAt: c.firstVisitAt ?? new Date(),
        firstVisitAt: c.firstVisitAt ?? undefined,
        lastVisitAt: c.lastVisitAt ?? undefined,
        // 夜職で意味のある列
        receivableBalance: c.receivableBalance,
        receivableCount: c.receivableCount,
        bottleCount: c.bottleCount,
        topStaffName: c.topStaffName,
        preferredDayOfWeek: undefined,
        preferredTimeSlot: undefined,
        averageLeadTime: null,
      }));

      // 予約に依存する分析は材料が無いので空にする。
      // 数字を作って埋めると、合わない数字が1つ増えるだけになる。
      setAllReservations([]);
      setCustomers(customersArray as any);
    } catch (error) {
      console.error('顧客一覧の取得エラー:', error);
      setError('顧客一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 顧客セグメント計算
  const customerSegments = useMemo((): CustomerSegment[] => {
    const now = new Date();
    const thirtyDaysAgo = subDays(now, 30);
    const ninetyDaysAgo = subDays(now, 90);
    const oneEightyDaysAgo = subDays(now, 180);

    let vip = 0;
    let regular = 0;
    let newCustomers = 0;
    let dormant = 0;
    let atRisk = 0;
    let churned = 0;

    customers.forEach(customer => {
      const daysSinceLastVisit = customer.lastVisitAt
        ? differenceInDays(now, customer.lastVisitAt)
        : 999;
      const totalVisits = customer.stats.completedReservations;
      const totalSpent = customer.stats.totalSpent;

      // VIP: 10回以上 or 累計10万円以上
      if (totalVisits >= 10 || totalSpent >= 100000) {
        vip++;
      }
      // 離脱危機: 180日以上来店なし & 過去来店あり
      else if (daysSinceLastVisit >= 180 && totalVisits > 0) {
        churned++;
      }
      // 休眠: 90-180日来店なし
      else if (daysSinceLastVisit >= 90 && daysSinceLastVisit < 180) {
        atRisk++;
      }
      // 新規: 初来店から30日以内
      else if (customer.firstVisitAt && differenceInDays(now, customer.firstVisitAt) <= 30) {
        newCustomers++;
      }
      // 休眠予備軍: 30-90日来店なし
      else if (daysSinceLastVisit >= 30 && daysSinceLastVisit < 90) {
        dormant++;
      }
      // レギュラー
      else {
        regular++;
      }
    });

    const total = customers.length || 1;

    return [
      {
        name: 'VIP',
        count: vip,
        percentage: (vip / total) * 100,
        color: SEGMENT_COLORS.vip,
        icon: <StarIcon />,
        description: '10回以上または累計10万円以上',
      },
      {
        name: 'レギュラー',
        count: regular,
        percentage: (regular / total) * 100,
        color: SEGMENT_COLORS.regular,
        icon: <PeopleIcon />,
        description: '定期的に来店',
      },
      {
        name: '新規',
        count: newCustomers,
        percentage: (newCustomers / total) * 100,
        color: SEGMENT_COLORS.new,
        icon: <NewReleasesIcon />,
        description: '初来店から30日以内',
      },
      {
        name: '休眠予備軍',
        count: dormant,
        percentage: (dormant / total) * 100,
        color: SEGMENT_COLORS.dormant,
        icon: <AccessTimeIcon />,
        description: '30-90日来店なし',
      },
      {
        name: '離脱危機',
        count: atRisk,
        percentage: (atRisk / total) * 100,
        color: SEGMENT_COLORS.atrisk,
        icon: <WarningIcon />,
        description: '90-180日来店なし',
      },
      {
        name: '離脱',
        count: churned,
        percentage: (churned / total) * 100,
        color: SEGMENT_COLORS.churned,
        icon: <DeleteIcon />,
        description: '180日以上来店なし',
      },
    ];
  }, [customers]);

  // 曜日別来店パターン
  const visitPatternByDay = useMemo((): VisitPattern[] => {
    const dayNames = ['日', '月', '火', '水', '木', '金', '土'];
    const counts = [0, 0, 0, 0, 0, 0, 0];

    allReservations
      .filter(r => r.status === 'completed' && r.reservationDate)
      .forEach(r => {
        const day = r.reservationDate.getDay();
        counts[day]++;
      });

    return dayNames.map((name, index) => ({
      dayOfWeek: name,
      count: counts[index],
    }));
  }, [allReservations]);

  // 時間帯別来店パターン
  const visitPatternByTime = useMemo((): TimeSlotPattern[] => {
    const slots: Record<string, number> = {
      '9-11時': 0,
      '11-13時': 0,
      '13-15時': 0,
      '15-17時': 0,
      '17-19時': 0,
      '19-21時': 0,
    };

    allReservations
      .filter(r => r.status === 'completed' && r.startTime)
      .forEach(r => {
        const hour = parseInt(r.startTime.split(':')[0]);
        if (hour >= 9 && hour < 11) slots['9-11時']++;
        else if (hour >= 11 && hour < 13) slots['11-13時']++;
        else if (hour >= 13 && hour < 15) slots['13-15時']++;
        else if (hour >= 15 && hour < 17) slots['15-17時']++;
        else if (hour >= 17 && hour < 19) slots['17-19時']++;
        else if (hour >= 19 && hour < 21) slots['19-21時']++;
      });

    return Object.entries(slots).map(([timeSlot, count]) => ({
      timeSlot,
      count,
    }));
  }, [allReservations]);

  // 月別新規・リピーター推移
  const monthlyCustomerStats = useMemo((): MonthlyStats[] => {
    const now = new Date();
    const sixMonthsAgo = subMonths(now, 6);
    const months = eachMonthOfInterval({ start: sixMonthsAgo, end: now });

    return months.map(month => {
      const monthStart = startOfMonth(month);
      const monthEnd = endOfMonth(month);

      const customersThisMonth = new Set<string>();
      const newCustomersThisMonth = new Set<string>();

      allReservations
        .filter(r =>
          r.status === 'completed' &&
          r.reservationDate &&
          r.reservationDate >= monthStart &&
          r.reservationDate <= monthEnd
        )
        .forEach(r => {
          customersThisMonth.add(r.userId);

          // 初回予約かどうかチェック
          const customer = customers.find(c => c.userId === r.userId);
          if (customer?.firstVisitAt &&
              customer.firstVisitAt >= monthStart &&
              customer.firstVisitAt <= monthEnd) {
            newCustomersThisMonth.add(r.userId);
          }
        });

      const total = customersThisMonth.size;
      const newCount = newCustomersThisMonth.size;
      const repeatCount = total - newCount;

      return {
        month: format(month, 'M月', { locale: ja }),
        newCustomers: newCount,
        repeatCustomers: repeatCount,
        totalCustomers: total,
        repeatRate: total > 0 ? (repeatCount / total) * 100 : 0,
      };
    });
  }, [allReservations, customers]);

  // リードタイム分布
  const leadTimeDistribution = useMemo((): LeadTimeDistribution[] => {
    const ranges: Record<string, number> = {
      '当日': 0,
      '1-3日前': 0,
      '4-7日前': 0,
      '1-2週間前': 0,
      '2週間以上前': 0,
    };

    allReservations
      .filter(r => r.status === 'completed' && r.createdAt && r.reservationDate)
      .forEach(r => {
        const leadTime = differenceInDays(r.reservationDate, r.createdAt);
        if (leadTime <= 0) ranges['当日']++;
        else if (leadTime <= 3) ranges['1-3日前']++;
        else if (leadTime <= 7) ranges['4-7日前']++;
        else if (leadTime <= 14) ranges['1-2週間前']++;
        else ranges['2週間以上前']++;
      });

    const total = Object.values(ranges).reduce((a, b) => a + b, 0) || 1;

    return Object.entries(ranges).map(([range, count]) => ({
      range,
      count,
      percentage: (count / total) * 100,
    }));
  }, [allReservations]);

  // LTV上位顧客
  const topCustomersByLTV = useMemo(() => {
    return [...customers]
      .sort((a, b) => b.stats.totalSpent - a.stats.totalSpent)
      .slice(0, 10);
  }, [customers]);

  // セグメントでフィルタリングされた顧客
  const filteredCustomersBySegment = useMemo(() => {
    if (segmentFilter === 'all') return customers;

    const now = new Date();

    return customers.filter(customer => {
      const daysSinceLastVisit = customer.lastVisitAt
        ? differenceInDays(now, customer.lastVisitAt)
        : 999;
      const totalVisits = customer.stats.completedReservations;
      const totalSpent = customer.stats.totalSpent;

      switch (segmentFilter) {
        case 'vip':
          return totalVisits >= 10 || totalSpent >= 100000;
        case 'regular':
          return daysSinceLastVisit < 30 && totalVisits < 10 && totalSpent < 100000;
        case 'new':
          return customer.firstVisitAt && differenceInDays(now, customer.firstVisitAt) <= 30;
        case 'dormant':
          return daysSinceLastVisit >= 30 && daysSinceLastVisit < 90;
        case 'atrisk':
          return daysSinceLastVisit >= 90 && daysSinceLastVisit < 180;
        case 'churned':
          return daysSinceLastVisit >= 180 && totalVisits > 0;
        default:
          return true;
      }
    });
  }, [customers, segmentFilter]);

  const fetchCustomerReservations = async (userId: string) => {
    if (!shop) return;

    try {
      const q = query(
        collection(db, 'reservations'),
        where('shopId', '==', shop.id),
        where('userId', '==', userId),
        orderBy('reservationDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        reservationDate: doc.data().reservationDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
      })) as CustomerReservation[];
      setCustomerReservations(data);
    } catch (error) {
      console.error('予約履歴の取得エラー:', error);
    }
  };

  const handleViewDetails = async (customer: Customer) => {
    setSelectedCustomer(customer);
    setOpenDialog(true);
    await fetchCustomerReservations(customer.userId);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCustomer(null);
    setCustomerReservations([]);
    setDetailTabValue(0);
  };

  const handleOpenCreateReservation = (customer: Customer) => {
    setSelectedCustomer(customer);
    setOpenCreateReservationDialog(true);
  };

  const handleCloseCreateReservation = () => {
    setOpenCreateReservationDialog(false);
    setSelectedCustomer(null);
  };

  const handleReservationCreated = () => {
    setSuccess('予約を作成しました');
    fetchCustomers();
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleOpenDeleteDialog = (customer: Customer) => {
    setCustomerToDelete(customer);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setCustomerToDelete(null);
  };

  /**
   * 顧客の削除。
   *
   * この画面の顧客は、会計・ツケ・ボトルキープに残った名前から作る台帳で、
   * 独立した顧客ドキュメントは存在しない。
   * 以前はここで `where('userId','==', ...)` を3本流していたが、
   * `userId` に入っているのは台帳のキー（客の名前）で、
   * checkouts には userId という項目自体が無い。
   * その結果、何も消さないまま「削除しました（予約0件、注文0件、会計0件）」と出て、
   * 一覧にもそのまま残っていた。
   *
   * さらに、仮に一致していたら会計を消すことになる。
   * 締めた売上を客単位で消せてしまうのは、POS として持ってはいけない機能。
   * 顧客を消したいなら、元になっている会計・ツケ・ボトルキープを
   * それぞれの画面で扱う。ここでは消せないことをはっきり伝える。
   */
  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    setError(
      `${customerToDelete.displayName} は会計・ツケ・ボトルキープから作られた台帳なので、` +
      'ここからは消せません。会計を消すと売上の記録が消えます。' +
      '来店の記録を直したい場合は、該当の伝票を「伝票の取消」から取り消してください。'
    );
    handleCloseDeleteDialog();
  };


  const filteredCustomers = useMemo(() => {
    let result = segmentFilter === 'all' ? customers : filteredCustomersBySegment;

    if (!searchTerm) return result;

    const searchLower = searchTerm.toLowerCase();
    return result.filter(customer =>
      customer.displayName.toLowerCase().includes(searchLower) ||
      customer.email.toLowerCase().includes(searchLower) ||
      customer.phoneNumber?.includes(searchTerm)
    );
  }, [customers, filteredCustomersBySegment, segmentFilter, searchTerm]);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return '承認待ち';
      case 'confirmed': return '確定';
      case 'completed': return '完了';
      case 'cancelled': return 'キャンセル';
      case 'no_show': return '無断キャンセル';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return '#FFA726';
      case 'confirmed': return '#29B6F6';
      case 'completed': return '#66BB6A';
      case 'cancelled': return '#BDBDBD';
      case 'no_show': return '#EF5350';
      default: return '#757575';
    }
  };

  const getTrustScore = (customer: Customer) => {
    const total = customer.stats.totalReservations;
    const cancelled = customer.stats.cancelledReservations;
    const noShow = customer.stats.noShowCount;

    if (total === 0) return 100;

    const score = 100 - ((cancelled * 5 + noShow * 10) / total * 100);
    return Math.max(0, Math.round(score));
  };

  const getTrustScoreColor = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const getDayOfWeekName = (day: number) => {
    const days = ['日', '月', '火', '水', '木', '金', '土'];
    return days[day];
  };

  const formatCurrency = (amount: number) => {
    const currency = shop?.currency || 'JPY';
    if (currency === 'THB') {
      return `฿${amount.toLocaleString()}`;
    }
    return `¥${amount.toLocaleString()}`;
  };

  // CSVエクスポート
  const exportCustomersCSV = () => {
    // 画面の列と揃える。片方だけ直すと、見えている表と出した CSV が食い違う。
    const headers = ['顧客名', '電話番号', '来店回数', '累計売上', '初来店日', '最終来店日',
                     'よく付いた担当', 'ツケ残高', 'ツケ件数', 'キープ本数'];
    const rows = customers.map((c: any) => [
      c.displayName,
      c.phoneNumber,
      c.stats.totalReservations,
      c.stats.totalSpent,
      c.firstVisitAt ? format(c.firstVisitAt, 'yyyy-MM-dd') : '',
      c.lastVisitAt ? format(c.lastVisitAt, 'yyyy-MM-dd') : '',
      c.topStaffName ?? '',
      c.receivableBalance ?? 0,
      c.receivableCount ?? 0,
      c.bottleCount ?? 0,
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `customers_${format(new Date(), 'yyyyMMdd')}.csv`;
    link.click();
  };

  if (loading && customers.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {isCheckoutSelectMode ? '会計用 - 顧客を選択' : '顧客管理'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isCheckoutSelectMode && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportCustomersCSV}
            >
              CSV出力
            </Button>
          )}
          {isCheckoutSelectMode && (
            <Button
              variant="outlined"
              onClick={() => navigate('/mobile-order/checkout')}
            >
              キャンセル
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Tabs */}
      {!isCheckoutSelectMode && (
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={mainTabValue}
            onChange={(e, newValue) => setMainTabValue(newValue)}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab icon={<PeopleIcon />} label="顧客一覧" iconPosition="start" />
            <Tab icon={<TrendingUpIcon />} label="分析" iconPosition="start" />
          </Tabs>
        </Paper>
      )}

      {/* 顧客一覧タブ */}
      {(mainTabValue === 0 || isCheckoutSelectMode) && (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    総顧客数
                  </Typography>
                  <Typography variant="h4">
                    {customers.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    ツケが残っている
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#FFA726' }}>
                    {customers.filter((c: any) => (c.receivableBalance ?? 0) > 0).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    リピーター（3回以上）
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#29B6F6' }}>
                    {customers.filter(c => c.stats.completedReservations >= 3).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    ボトルをキープ中
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#AB47BC' }}>
                    {customers.filter((c: any) => (c.bottleCount ?? 0) > 0).length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Segment Filter & Search */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>セグメント</InputLabel>
                  <Select
                    value={segmentFilter}
                    label="セグメント"
                    onChange={(e) => setSegmentFilter(e.target.value)}
                  >
                    <MenuItem value="all">すべて</MenuItem>
                    <MenuItem value="vip">VIP</MenuItem>
                    <MenuItem value="regular">レギュラー</MenuItem>
                    <MenuItem value="new">新規</MenuItem>
                    <MenuItem value="dormant">休眠予備軍</MenuItem>
                    <MenuItem value="atrisk">離脱危機</MenuItem>
                    <MenuItem value="churned">離脱</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="顧客名、メールアドレス、電話番号で検索"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Customers Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>顧客名</TableCell>
                  <TableCell>連絡先</TableCell>
                  <TableCell>来店回数</TableCell>
                  <TableCell>累計売上</TableCell>
                  <TableCell>最終来店</TableCell>
                  <TableCell>よく付いた担当</TableCell>
                  <TableCell>ツケ</TableCell>
                  <TableCell>キープ</TableCell>
                  <TableCell align="right">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      読み込み中...
                    </TableCell>
                  </TableRow>
                ) : filteredCustomers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      {customers.length === 0 ? '顧客がいません' : '条件に一致する顧客がいません'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCustomers.map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {customer.displayName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{customer.email}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {customer.phoneNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {customer.stats.totalReservations}回
                          </Typography>
                          {customer.stats.cancelledReservations > 0 && (
                            <Typography variant="caption" color="warning.main">
                              キャンセル: {customer.stats.cancelledReservations}回
                            </Typography>
                          )}
                          {customer.stats.noShowCount > 0 && (
                            <Typography variant="caption" color="error.main" display="block">
                              無断: {customer.stats.noShowCount}回
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(customer.stats.totalSpent)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {customer.lastVisitAt ? (
                          <Typography variant="body2">
                            {format(customer.lastVisitAt, 'yyyy/MM/dd')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            未来店
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {(customer as any).topStaffName ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {((customer as any).receivableBalance ?? 0) > 0 ? (
                          <Chip
                            label={`${formatCurrency((customer as any).receivableBalance)} / ${(customer as any).receivableCount}件`}
                            color="warning"
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {((customer as any).bottleCount ?? 0) > 0 ? (
                          <Chip label={`${(customer as any).bottleCount}本`} size="small" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {isCheckoutSelectMode ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSelectForCheckout(customer)}
                            color="primary"
                          >
                            選択
                          </Button>
                        ) : (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(customer)}
                              color="primary"
                              title="詳細を表示"
                            >
                              <VisibilityIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCreateReservation(customer)}
                              color="success"
                              title="予約を作成"
                            >
                              <ScheduleIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(customer)}
                              color="error"
                              title="削除"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* 分析タブ */}
      {mainTabValue === 1 && !isCheckoutSelectMode && (
        <>
          {/* Analytics Sub-Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={analyticsSubTab}
              onChange={(e, newValue) => setAnalyticsSubTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="セグメント分析" />
              <Tab label="来店パターン" />
              <Tab label="リピート分析" />
              <Tab label="LTV分析" />
            </Tabs>
          </Paper>

          {/* セグメント分析 */}
          {analyticsSubTab === 0 && (
            <Grid container spacing={3}>
              {/* セグメント円グラフ */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: 400 }}>
                  <Typography variant="h6" gutterBottom>顧客セグメント構成</Typography>
                  <ResponsiveContainer width="100%" height="90%">
                    <PieChart>
                      <Pie
                        data={customerSegments.filter(s => s.count > 0)}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={({ name, percent }) => `${name} (${((percent || 0) * 100).toFixed(0)}%)`}
                      >
                        {customerSegments.filter(s => s.count > 0).map((entry, index) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* セグメント詳細カード */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: 400, overflow: 'auto' }}>
                  <Typography variant="h6" gutterBottom>セグメント詳細</Typography>
                  <Grid container spacing={2}>
                    {customerSegments.map((segment) => (
                      <Grid item xs={12} sm={6} key={segment.name}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderLeft: `4px solid ${segment.color}`,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' }
                          }}
                          onClick={() => {
                            setSegmentFilter(segment.name === 'VIP' ? 'vip' :
                              segment.name === 'レギュラー' ? 'regular' :
                              segment.name === '新規' ? 'new' :
                              segment.name === '休眠予備軍' ? 'dormant' :
                              segment.name === '離脱危機' ? 'atrisk' :
                              segment.name === '離脱' ? 'churned' : 'all');
                            setMainTabValue(0);
                          }}
                        >
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Box sx={{ color: segment.color }}>{segment.icon}</Box>
                              <Typography variant="subtitle2">{segment.name}</Typography>
                            </Box>
                            <Typography variant="h5">{segment.count}人</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {segment.description}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* 来店パターン */}
          {analyticsSubTab === 1 && (
            <Grid container spacing={3}>
              {/* 曜日別 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: 350 }}>
                  <Typography variant="h6" gutterBottom>曜日別来店数</Typography>
                  <ResponsiveContainer width="100%" height="85%">
                    <BarChart data={visitPatternByDay}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="dayOfWeek" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="count" fill="#2196F3" name="来店数" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* 時間帯別 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: 350 }}>
                  <Typography variant="h6" gutterBottom>時間帯別来店数</Typography>
                  <ResponsiveContainer width="100%" height="85%">
                    <BarChart data={visitPatternByTime}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="timeSlot" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="count" fill="#4CAF50" name="来店数" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* リードタイム分布 */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, height: 350 }}>
                  <Typography variant="h6" gutterBottom>予約リードタイム分布（予約から来店までの日数）</Typography>
                  <ResponsiveContainer width="100%" height="85%">
                    <BarChart data={leadTimeDistribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="range" />
                      <YAxis />
                      <RechartsTooltip
                        formatter={(value: any, name: any) => {
                          if (name === 'percentage') return `${value.toFixed(1)}%`;
                          return value;
                        }}
                      />
                      <Bar dataKey="count" fill="#FF9800" name="予約数" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* リピート分析 */}
          {analyticsSubTab === 2 && (
            <Grid container spacing={3}>
              {/* 月別新規・リピーター推移 */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, height: 400 }}>
                  <Typography variant="h6" gutterBottom>月別 新規・リピーター推移</Typography>
                  <ResponsiveContainer width="100%" height="85%">
                    <AreaChart data={monthlyCustomerStats}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                      <RechartsTooltip />
                      <Legend />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="newCustomers"
                        stackId="1"
                        stroke="#2196F3"
                        fill="#2196F3"
                        name="新規顧客"
                      />
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="repeatCustomers"
                        stackId="1"
                        stroke="#4CAF50"
                        fill="#4CAF50"
                        name="リピーター"
                      />
                      <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey="repeatRate"
                        stroke="#FF9800"
                        name="リピート率(%)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* リピート回数分布 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>来店回数別顧客数</Typography>
                  <Box sx={{ mt: 2 }}>
                    {[
                      { label: '1回', min: 1, max: 1 },
                      { label: '2-3回', min: 2, max: 3 },
                      { label: '4-5回', min: 4, max: 5 },
                      { label: '6-10回', min: 6, max: 10 },
                      { label: '11回以上', min: 11, max: Infinity },
                    ].map(range => {
                      const count = customers.filter(
                        c => c.stats.completedReservations >= range.min &&
                             c.stats.completedReservations <= range.max
                      ).length;
                      const percentage = customers.length > 0 ? (count / customers.length) * 100 : 0;

                      return (
                        <Box key={range.label} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{range.label}</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {count}人 ({percentage.toFixed(1)}%)
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{ height: 8, borderRadius: 4 }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>

              {/* リピート間隔 */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>主要指標</Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">平均来店回数</Typography>
                          <Typography variant="h4">
                            {customers.length > 0
                              ? (customers.reduce((sum, c) => sum + c.stats.completedReservations, 0) / customers.length).toFixed(1)
                              : 0}回
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">リピート率</Typography>
                          <Typography variant="h4">
                            {customers.length > 0
                              ? ((customers.filter(c => c.stats.completedReservations >= 2).length / customers.length) * 100).toFixed(1)
                              : 0}%
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">平均リードタイム</Typography>
                          <Typography variant="h4">
                            {customers.filter(c => c.averageLeadTime !== null).length > 0
                              ? (customers.reduce((sum, c) => sum + (c.averageLeadTime || 0), 0) /
                                 customers.filter(c => c.averageLeadTime !== null).length).toFixed(1)
                              : 0}日
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">キャンセル率</Typography>
                          <Typography variant="h4" color="warning.main">
                            {allReservations.length > 0
                              ? ((allReservations.filter(r => r.status === 'cancelled').length / allReservations.length) * 100).toFixed(1)
                              : 0}%
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* LTV分析 */}
          {analyticsSubTab === 3 && (
            <Grid container spacing={3}>
              {/* LTV上位顧客 */}
              <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>累計売上 TOP10</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>順位</TableCell>
                          <TableCell>顧客名</TableCell>
                          <TableCell align="right">来店回数</TableCell>
                          <TableCell align="right">累計売上</TableCell>
                          <TableCell align="right">客単価</TableCell>
                          <TableCell align="right">最終来店</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {topCustomersByLTV.map((customer, index) => (
                          <TableRow
                            key={customer.id}
                            hover
                            onClick={() => handleViewDetails(customer)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <Chip
                                label={index + 1}
                                size="small"
                                color={index < 3 ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{customer.displayName}</TableCell>
                            <TableCell align="right">{customer.stats.completedReservations}回</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(customer.stats.totalSpent)}
                            </TableCell>
                            <TableCell align="right">
                              {customer.stats.completedReservations > 0
                                ? formatCurrency(Math.round(customer.stats.totalSpent / customer.stats.completedReservations))
                                : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {customer.lastVisitAt
                                ? format(customer.lastVisitAt, 'yyyy/MM/dd')
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>

              {/* LTV統計 */}
              <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>LTV統計</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">総売上</Typography>
                          <Typography variant="h4">
                            {formatCurrency(customers.reduce((sum, c) => sum + c.stats.totalSpent, 0))}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">顧客平均LTV</Typography>
                          <Typography variant="h4">
                            {customers.length > 0
                              ? formatCurrency(Math.round(customers.reduce((sum, c) => sum + c.stats.totalSpent, 0) / customers.length))
                              : formatCurrency(0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">平均客単価</Typography>
                          <Typography variant="h4">
                            {allReservations.filter(r => r.status === 'completed').length > 0
                              ? formatCurrency(Math.round(
                                  allReservations
                                    .filter(r => r.status === 'completed')
                                    .reduce((sum, r) => sum + (r.totalPrice || 0), 0) /
                                  allReservations.filter(r => r.status === 'completed').length
                                ))
                              : formatCurrency(0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary">VIP顧客売上比率</Typography>
                          <Typography variant="h4">
                            {(() => {
                              const vipCustomers = customers.filter(
                                c => c.stats.completedReservations >= 10 || c.stats.totalSpent >= 100000
                              );
                              const vipRevenue = vipCustomers.reduce((sum, c) => sum + c.stats.totalSpent, 0);
                              const totalRevenue = customers.reduce((sum, c) => sum + c.stats.totalSpent, 0);
                              return totalRevenue > 0 ? `${((vipRevenue / totalRevenue) * 100).toFixed(1)}%` : '0%';
                            })()}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* 売上分布 */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>累計売上帯別 顧客分布</Typography>
                  <Box sx={{ mt: 2 }}>
                    {[
                      { label: '〜¥10,000', min: 0, max: 10000 },
                      { label: '¥10,001〜¥30,000', min: 10001, max: 30000 },
                      { label: '¥30,001〜¥50,000', min: 30001, max: 50000 },
                      { label: '¥50,001〜¥100,000', min: 50001, max: 100000 },
                      { label: '¥100,001以上', min: 100001, max: Infinity },
                    ].map(range => {
                      const count = customers.filter(
                        c => c.stats.totalSpent >= range.min && c.stats.totalSpent <= range.max
                      ).length;
                      const percentage = customers.length > 0 ? (count / customers.length) * 100 : 0;
                      const revenue = customers
                        .filter(c => c.stats.totalSpent >= range.min && c.stats.totalSpent <= range.max)
                        .reduce((sum, c) => sum + c.stats.totalSpent, 0);

                      return (
                        <Box key={range.label} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2">{range.label}</Typography>
                            <Typography variant="body2">
                              <strong>{count}人</strong> ({percentage.toFixed(1)}%) / 売上: {formatCurrency(revenue)}
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{ height: 8, borderRadius: 4 }}
                            color="primary"
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* Customer Details Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>顧客詳細</DialogTitle>
        <DialogContent>
          {selectedCustomer && (
            <Box sx={{ mt: 2 }}>
              <Tabs value={detailTabValue} onChange={(e, newValue) => setDetailTabValue(newValue)} sx={{ mb: 2 }}>
                <Tab label="基本情報" />
                <Tab label="予約履歴" />
                <Tab label="統計" />
                <Tab label="傾向分析" />
              </Tabs>

              {detailTabValue === 0 && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">お名前</Typography>
                      <Typography variant="body1" fontWeight="bold" gutterBottom>
                        {selectedCustomer.displayName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">メールアドレス</Typography>
                      <Typography variant="body1" gutterBottom>{selectedCustomer.email}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">電話番号</Typography>
                      <Typography variant="body1" gutterBottom>{selectedCustomer.phoneNumber}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">LINE連携</Typography>
                      <Chip
                        label={selectedCustomer.userLineId ? '連携済み' : '未連携'}
                        color={selectedCustomer.userLineId ? 'success' : 'default'}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">初回来店日</Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedCustomer.firstVisitAt
                          ? format(selectedCustomer.firstVisitAt, 'yyyy年M月d日')
                          : '未来店'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color="text.secondary">最終来店日</Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedCustomer.lastVisitAt
                          ? format(selectedCustomer.lastVisitAt, 'yyyy年M月d日')
                          : '未来店'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {detailTabValue === 1 && (
                <Box>
                  {customerReservations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      予約履歴がありません
                    </Typography>
                  ) : (
                    <List>
                      {customerReservations.map((reservation, index) => (
                        <React.Fragment key={reservation.id}>
                          {index > 0 && <Divider />}
                          <ListItem>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2" fontWeight="bold">
                                    {format(reservation.reservationDate, 'yyyy年M月d日')} {reservation.startTime}
                                  </Typography>
                                  <Chip
                                    label={getStatusLabel(reservation.status)}
                                    size="small"
                                    sx={{
                                      backgroundColor: getStatusColor(reservation.status),
                                      color: 'white'
                                    }}
                                  />
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="caption" display="block">
                                    {reservation.menuName} / {reservation.staffName || '指定なし'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatCurrency(reservation.totalPrice)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </Box>
              )}

              {detailTabValue === 2 && (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            総予約回数
                          </Typography>
                          <Typography variant="h4">
                            {selectedCustomer.stats.totalReservations}回
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            累計売上
                          </Typography>
                          <Typography variant="h4">
                            {formatCurrency(selectedCustomer.stats.totalSpent)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            信頼度スコア
                          </Typography>
                          <Typography variant="h4" color={getTrustScoreColor(getTrustScore(selectedCustomer)) + '.main'}>
                            {getTrustScore(selectedCustomer)}%
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            平均客単価
                          </Typography>
                          <Typography variant="h4">
                            {selectedCustomer.stats.completedReservations > 0
                              ? formatCurrency(Math.round(selectedCustomer.stats.totalSpent / selectedCustomer.stats.completedReservations))
                              : '-'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            キャンセル回数
                          </Typography>
                          <Typography variant="h4" color="warning.main">
                            {selectedCustomer.stats.cancelledReservations}回
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            無断キャンセル回数
                          </Typography>
                          <Typography variant="h4" color="error.main">
                            {selectedCustomer.stats.noShowCount}回
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {detailTabValue === 3 && (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            よく来店する曜日
                          </Typography>
                          <Typography variant="h4">
                            {selectedCustomer.preferredDayOfWeek !== undefined
                              ? getDayOfWeekName(selectedCustomer.preferredDayOfWeek) + '曜日'
                              : '-'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            よく来店する時間帯
                          </Typography>
                          <Typography variant="h4">
                            {selectedCustomer.preferredTimeSlot || '-'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            平均予約リードタイム
                          </Typography>
                          <Typography variant="h4">
                            {selectedCustomer.averageLeadTime !== null && selectedCustomer.averageLeadTime !== undefined
                              ? `${selectedCustomer.averageLeadTime.toFixed(1)}日前`
                              : '-'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            顧客期間
                          </Typography>
                          <Typography variant="h4">
                            {selectedCustomer.firstVisitAt
                              ? `${differenceInDays(new Date(), selectedCustomer.firstVisitAt)}日`
                              : '-'}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedCustomer && (
            <Button
              onClick={() => {
                handleCloseDialog();
                handleOpenCreateReservation(selectedCustomer);
              }}
              variant="contained"
              startIcon={<ScheduleIcon />}
            >
              予約を作成
            </Button>
          )}
          <Button onClick={handleCloseDialog}>閉じる</Button>
        </DialogActions>
      </Dialog>

      {/* Create Reservation Dialog */}
      <CreateReservationDialog
        open={openCreateReservationDialog}
        customer={selectedCustomer}
        onClose={handleCloseCreateReservation}
        onSuccess={handleReservationCreated}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>この店舗での顧客データを削除</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            この操作は取り消せません。本当に削除しますか？
          </Alert>
          {customerToDelete && (
            <Box>
              <Typography variant="body1" gutterBottom>
                以下の顧客のこの店舗でのデータを削除します：
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {customerToDelete.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {customerToDelete.email}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                予約回数: {customerToDelete.stats.totalReservations}回
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                削除されるデータ: 予約履歴、注文履歴、会計履歴<br />
                ※ 他店舗のデータやユーザーアカウント自体は削除されません
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>キャンセル</Button>
          <Button onClick={handleDeleteCustomer} color="error" variant="contained">
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Customers;
